import { isRequestError } from 'utils/requestError';
import { InputPinCode, InputText, Loading } from 'components';
import { LocalStorageKey } from 'configs/constants';
import { Roles } from 'configs/roles';
import { Button, InputWraper, useForm } from 'modules';
import React, { useEffect, useRef, useState } from 'react';
import toast from 'react-hot-toast';
import { useNavigate } from 'react-router-dom';
import { AuthRouteKeys, MainRouteKeys } from 'routes/enums';
import {
  useCheckPhoneNoQuery,
  useGetAdminActionsQuery,
  useLoginMutation,
  useVerifyTwoFaMutation,
} from 'services';
import { getUserRoles, hasAuthenticatedSession } from 'utils/helpers';

import { formSchema } from './config';
import ModalRefferal from './ModalRefferal';
import * as Yup from 'yup';

type FormData = {
  phoneNumber: string;
  pinCode: string;
};

type MfaChallenge = {
  mfaToken: string;
  expiresIn: number;
  method?: 'totp';
};

type AuthenticatedResult = {
  token?: string;
  isFirstLogin?: boolean;
  isActiveReferralProgram?: boolean;
};

const LoginFeatures = () => {
  const navigate = useNavigate();
  const isMountedRef = useRef(true);

  const [phoneNumber, setPhoneNumber] = useState<string>('');
  const [mfaChallenge, setMfaChallenge] = useState<MfaChallenge>();
  const [mfaCountdown, setMfaCountdown] = useState(0);
  const [mfaAttemptsRemaining, setMfaAttemptsRemaining] = useState<number>();

  const [isVisible, setIsVisible] = useState<boolean>(false);

  const [isVisibleModalRefferal, setIsVisibleModalRefferal] =
    useState<boolean>(false);

  const { data, isFetching, refetch } = useCheckPhoneNoQuery({
    phoneNumber,
  });

  const { refetch: refetchAdminActions } = useGetAdminActionsQuery();

  const { mutateAsync } = useLoginMutation();
  const { mutateAsync: verifyTwoFa, isLoading: isVerifyingTwoFa } =
    useVerifyTwoFaMutation();

  useEffect(() => {
    isMountedRef.current = true;

    return () => {
      isMountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    if (phoneNumber && phoneNumber.length >= 10) {
      refetch();
    }
  }, [phoneNumber]);

  useEffect(() => {
    if (data && data.statusCode === 201) {
      setIsVisible(true);
      toast.success('Bạn chưa có mật khẩu, vui lòng tạo mật khẩu mới');
    } else {
      setIsVisible(false);
    }
  }, [data]);

  useEffect(() => {
    setMfaCountdown(mfaChallenge?.expiresIn || 0);
  }, [mfaChallenge]);

  useEffect(() => {
    if (mfaCountdown <= 0) return;

    const timeout = window.setTimeout(() => {
      setMfaCountdown((value) => Math.max(value - 1, 0));
    }, 1000);

    return () => window.clearTimeout(timeout);
  }, [mfaCountdown]);

  const handleForgotPassword = () => {
    navigate(AuthRouteKeys.OTP, { state: { phoneNumber } });
  };

  const handleAuthenticatedResult = async (authResult: AuthenticatedResult) => {
    // The web login response no longer contains a JWT. The cookie set by the
    // API is sent automatically by the request clients.
    const isAuthenticated = await hasAuthenticatedSession();
    if (isAuthenticated && getUserRoles().includes(Roles.ADMIN)) {
      const data = await refetchAdminActions();
      if (!isMountedRef.current) return;
      if (data.isError) {
        localStorage.removeItem(LocalStorageKey.ADMIN_ACTIONS);
      } else {
        localStorage.setItem(
          LocalStorageKey.ADMIN_ACTIONS,
          JSON.stringify(data?.data?.adminActions || []),
        );
      }
    } else {
      localStorage.removeItem(LocalStorageKey.ADMIN_ACTIONS);
    }

    if (!isMountedRef.current) return;

    // Notify other tabs that a new session has started so they reload
    localStorage.setItem(LocalStorageKey.SESSION_MARKER, Date.now().toString());

    // Handle Enter Refferal Code
    if (!!authResult.isFirstLogin && !!authResult.isActiveReferralProgram) {
      setIsVisibleModalRefferal(true);
    } else {
      navigate(MainRouteKeys.Personal, { replace: true });
    }
  };

  const handleSubmitForm = async (values: FormData) => {
    if (data?.statusCode === 201) {
      return navigate(AuthRouteKeys.OTP, {
        state: { phoneNumber, onlyAllowPhone: true },
      });
    }
    try {
      const dataLoginMutation = await mutateAsync(values);
      if (!isMountedRef.current) return;

      if (dataLoginMutation.mfaRequired) {
        if (!dataLoginMutation.mfaToken) {
          toast.error('Không thể tạo phiên xác thực 2FA');
          return;
        }

        setMfaChallenge({
          mfaToken: dataLoginMutation.mfaToken,
          expiresIn: dataLoginMutation.expiresIn || 0,
          method: dataLoginMutation.method,
        });
        setMfaAttemptsRemaining(undefined);
        toast.success('Vui lòng nhập mã 2FA để hoàn tất đăng nhập');
        return;
      }

      await handleAuthenticatedResult(dataLoginMutation);
    } catch (error: any) {
      if (!isRequestError(error)) {
        toast.error(error?.message || 'Đăng nhập thất bại');
      }
    }
  };

  const { handleSubmit, getInputProps, isSubmitting } = useForm({
    structure: formSchema(!isVisible),
    onSubmit: handleSubmitForm,
  });

  const {
    handleSubmit: handleSubmitMfa,
    getInputProps: getMfaInputProps,
    resetForm: resetMfaForm,
  } = useForm({
    structure: [
      {
        name: 'otpCode',
        validate: Yup.string()
          .required('Vui lòng nhập mã 2FA')
          .length(6, 'Mã 2FA phải gồm 6 số'),
      },
    ],
    onSubmit: async (values) => {
      if (!mfaChallenge) return;
      if (mfaCountdown <= 0) {
        toast.error('Phiên xác thực 2FA đã hết hạn, vui lòng đăng nhập lại');
        return;
      }

      try {
        const response = await verifyTwoFa({
          mfaToken: mfaChallenge.mfaToken,
          otpCode: values.otpCode,
        });

        if (!response || response.statusCode >= 400) {
          setMfaAttemptsRemaining(response.data?.attemptsRemaining);
          toast.error('Mã 2FA không hợp lệ');
          return;
        }

        await handleAuthenticatedResult(response);
      } catch (error: any) {
        if (!isRequestError(error)) {
          toast.error(error?.message || 'Xác thực 2FA thất bại');
        }
      }
    },
  });

  const handleResetMfaChallenge = () => {
    setMfaChallenge(undefined);
    setMfaCountdown(0);
    setMfaAttemptsRemaining(undefined);
    resetMfaForm();
  };

  return (
    <div className='login-form'>
      <img
        className='login-form__logo'
        src='/assets/images/logo-white.png'
        alt='MLPay'
      />
      <div className='login-form__intro'>
        <h2>{mfaChallenge ? 'Xác thực 2FA' : 'Đăng nhập'}</h2>
        <p className='mb-2'>
          {mfaChallenge
            ? 'Nhập mã để tiếp tục.'
            : 'Tiếp tục vào không gian quản trị.'}
        </p>
      </div>

      {mfaChallenge ? (
        <form onSubmit={handleSubmitMfa} autoComplete='off'>
          <InputWraper
            label='Mã xác thực 2FA'
            inputProps={{
              ...getMfaInputProps('otpCode'),
              inputMode: 'numeric',
              pattern: '[0-9]*',
            }}
            component={InputPinCode}
            isRequired
          />
          <div
            style={{
              color: 'var(--text-muted)',
              fontSize: 13,
              marginBottom: 16,
            }}
          >
            {mfaCountdown > 0
              ? `Phiên xác thực còn ${mfaCountdown}s`
              : 'Phiên xác thực đã hết hạn'}
            {typeof mfaAttemptsRemaining === 'number'
              ? ` · Còn ${mfaAttemptsRemaining} lần thử`
              : ''}
          </div>

          <div className='login-form__actions'>
            <Button
              label='Xác thực 2FA'
              type='submit'
              buttonType='success'
              isLoading={isVerifyingTwoFa}
              disabled={mfaCountdown <= 0}
            />
            <Button
              label='Đăng nhập lại'
              type='button'
              onClick={handleResetMfaChallenge}
              buttonType='primary-outline'
            />
          </div>
        </form>
      ) : (
        <form onSubmit={handleSubmit} autoComplete='on'>
          <Loading isLoading={isFetching}>
            <InputWraper
              label='Số điện thoại'
              inputProps={getInputProps('phoneNumber')}
              onChangeCallback={setPhoneNumber}
              component={InputText}
              autoComplete='tel'
              isRequired={true}
            />

            <InputWraper
              label='Mật khẩu'
              inputProps={{
                ...getInputProps('pinCode'),
                pinCodeMode: 'single',
                maxPinLength: 20,
              }}
              component={InputPinCode}
              autoComplete='current-password'
              isVisible={isVisible}
              isRequired={!isVisible}
            />
          </Loading>

          <div className='login-form__actions'>
            <Button
              label={data?.statusCode === 201 ? 'Thêm mật khẩu' : 'Đăng nhập'}
              type='submit'
              buttonType='success'
              isLoading={isSubmitting}
            />
            <Button
              label='Quên mật khẩu'
              type='button'
              onClick={handleForgotPassword}
              buttonType='primary-outline'
            />
          </div>
        </form>
      )}
      <ModalRefferal
        isOpen={isVisibleModalRefferal}
        onSuccess={() => {
          // Handle successful referral code entry
          setIsVisibleModalRefferal(false);
          navigate(MainRouteKeys.Personal, { replace: true });
        }}
        onClose={() => {
          setIsVisibleModalRefferal(false);
          navigate(MainRouteKeys.Personal, { replace: true });
        }}
      />
    </div>
  );
};

export default LoginFeatures;
